/**
 * existingCustomer — derived "is this person an existing CIF on this cart"
 * flag + Verify-tab status rollup. (BACKLOG-0513 / SPEC.md Addendum 5 § A5.1.)
 *
 * Rule: when an FSR opens a pre-existing CIF on a new cart, the PDW collapses
 * to Step 2 (Cohort confirm, if ambiguous) + Step 9 (Review form). The fresh-
 * add CIP doc-capture loop is skipped, and the LN / OFAC auto-run side-effects
 * MUST NOT fire on tab mount. Re-run buttons stay visible — they're the only
 * way to trigger a fresh LN / OFAC run for existing customers.
 *
 * Verify tab status derivation (per spec + F1 2026-06-03):
 *   - red   HARD-BLOCK if primary ID expired AND not re-captured
 *   - red   HARD-BLOCK if a prior OFAC scan flagged a current SDN match
 *   - yellow         if primary ID expires within `idExpirationWarningDays`
 *                    (SystemSettings; default 60)
 *   - yellow         if an ACTUAL adverse prior result is on file (LN score
 *                    below the safe band, or OFAC scan still pending)
 *   - green          otherwise — existing CIF trusts the prior-pass baseline;
 *                    LN/OFAC are NOT re-required, so their ABSENCE never
 *                    downgrades (CIP-expiration is the only standing gate)
 *
 * The fresh-add code path is left intact: when `isExistingCustomer` is false,
 * the legacy auto-run + capture behaviour is unchanged.
 */

import type { RaoPerson } from '../../../types';
import { normalizeOfacStatus, type OfacStatus } from './ofacStatus';
import type { VerifyClearanceMap, SectionClearance } from './verifyClearance';
import { deriveFraudPointFollowUp } from '../individual/fraudPointFollowUp';

/**
 * Default for `SystemSettings.idExpirationWarningDays` when the setting
 * isn't seeded yet. Per spec brief. Surfaced as a constant so the
 * admin-config editor + this rule stay in sync.
 *
 * TODO(BACKLOG-followup): once `SystemSettings.idExpirationWarningDays`
 * lands in the SQL config catalog + admin editor, point this default at
 * the canonical lookup row instead of carrying a TS literal.
 */
export const DEFAULT_ID_EXPIRATION_WARNING_DAYS = 60;

/**
 * True when this Person represents a pre-existing CIF loaded onto the
 * current cart (CustInq result or BACKLOG-0509b/c existing-CIF entry path).
 * Drives the reduced PDW behaviour: skip auto LN/OFAC, skip CIP loop,
 * jump straight to the Review form.
 *
 * Detection (per BACKLOG-0513 brief):
 *   - person.cifId is populated (non-empty trimmed), AND
 *   - at least one of:
 *       · person.amlInsightScore is set (prior LN result on file), OR
 *       · person.extra.ofacStatus is a non-'unrun' status, OR
 *       · person.extra.cifSourceMode === 'existing-cif-search-result'
 *         (set by GlobalCustomerSearch when an FSR picks a CustSrch hit;
 *         leaves room for a future explicit marker if a CIF has neither
 *         a prior LN score nor an OFAC scan on file).
 *
 * `mode === 'existing'` is a related but distinct signal — it tracks the
 * cart commit branch (CustAdd vs CustMod), not whether the PDW should
 * collapse. A brand-new entry that's been saved once will have mode
 * 'existing' on re-open, but should still run the full PDW.
 */
/**
 * `extra.cifSourceMode` values that prove the person was loaded FROM Core
 * Director rather than typed in. Any of these + a cifId ⇒ existing customer.
 *
 * Bill 2026-07-15: `'existing-cif-search-result'` was written NOWHERE in the
 * repo — the only references were this predicate and its own docstring, so that
 * branch was dead and `isExistingCustomer` could only ever return true via an
 * LN score or an OFAC scan. That made it CIRCULAR: the existing-customer path
 * exists to SKIP LN + OFAC (feedback_existing_customer_skip_ln_and_ofac_20260521),
 * so a clean CIF with neither on file could never qualify as existing, and both
 * "Add Existing" and "Edit CIF" demanded a fresh ID capture for customers whose
 * CIP was already on file and unexpired. The hydration adapters now stamp these.
 */
const EXISTING_CIF_SOURCE_MODES: ReadonlySet<string> = new Set([
  /** RAO "Add Existing" — hydrated from a CustSrch hit (useInquiryHydration). */
  'existing-cif-search-result',
  /** RCM "Edit CIF" — hydrated from a direct CustInq load (custInqToRaoPerson). */
  'core-director-cif-load',
]);

export function isExistingCustomer(person: RaoPerson): boolean {
  const cifId = (person.cifId ?? '').trim();
  if (cifId.length === 0) return false;

  const extra = person.extra ?? {};

  // Provenance first — it's the only signal that doesn't depend on a screening
  // artifact we deliberately don't run for these customers.
  const sourceMode = typeof extra.cifSourceMode === 'string' ? extra.cifSourceMode : undefined;
  if (sourceMode && EXISTING_CIF_SOURCE_MODES.has(sourceMode)) return true;

  // Corroborating signals — a CIF that already carries a screening result is
  // self-evidently pre-existing. Kept for carts hydrated before the marker landed.
  if (typeof person.amlInsightScore === 'number') return true;

  const ofacRaw = typeof extra.ofacStatus === 'string' ? extra.ofacStatus : undefined;
  if (ofacRaw && normalizeOfacStatus(ofacRaw) !== 'unrun') return true;

  return false;
}

/**
 * Read `SystemSettings.idExpirationWarningDays` defensively from the
 * config bundle. The systemSettings shape may be a flat record or an
 * array-of-{key,value} depending on which seed has landed in SQL; both
 * are probed. Falls back to {@link DEFAULT_ID_EXPIRATION_WARNING_DAYS}.
 */
export function readIdExpirationWarningDays(systemSettings: unknown): number {
  const probe = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
    if (typeof v === 'string') {
      const n = parseInt(v, 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  };

  // Tolerate the array-of-{settingKey,settingValue} runtime shape (what
  // config.systemSettings actually is), a flat record, and legacy {key,value}.
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = typeof row.settingKey === 'string' ? row.settingKey
        : typeof row.key === 'string' ? row.key : undefined;
      if (k === 'idExpirationWarningDays') {
        const n = probe(row.settingValue ?? row.value);
        if (n !== null) return n;
      }
    }
  } else if (systemSettings && typeof systemSettings === 'object') {
    const rec = systemSettings as Record<string, unknown>;
    const n = probe(rec['idExpirationWarningDays']);
    if (n !== null) return n;
  }
  return DEFAULT_ID_EXPIRATION_WARNING_DAYS;
}

// ── Existing-customer Primary Photo ID gating (Bill 2026-07-08) ────────────
// For an EXISTING customer opening in-branch, the Primary Photo ID doc is:
//   • expired OR no ID on file  → REQUIRED (must capture a current ID)
//   • expiring within the window → ADMIN-CONFIGURABLE: 'hard-stop' (required)
//                                  or 'recommend' (advisory, not a Continue-
//                                  block). Default 'recommend' per Bill.
//   • valid, outside the window  → SATISFIED (on file at Core; capture only if
//                                  updating)
// A fresh add always REQUIRES capture. Keyed off the ID expiry (CD F258) that
// the existing-CIF load already populates.

export type ExistingIdExpiringSoonGate = 'hard-stop' | 'recommend';
export const DEFAULT_EXISTING_ID_EXPIRING_SOON_GATE: ExistingIdExpiringSoonGate = 'recommend';

export type PrimaryIdGating = 'required' | 'recommended' | 'satisfied';

/** SystemSettings key for the admin-configurable expiring-soon strategy. */
export const EXISTING_ID_EXPIRING_SOON_GATE_KEY = 'cip.existingCustomer.idExpiringSoon.gate';

/**
 * SystemSettings key for the admin-configurable "accept recently-expired ID"
 * grace window, in MONTHS (WT-BACKLOG C2). An existing customer whose primary
 * Photo ID expired within this many months is treated as 'recommended' (yellow
 * "Update recommended" + follow-up) rather than 'required' (red hard-block).
 * 0 = strict (any expired ID blocks). Default 6 per Collette (Deposit-App
 * walkthrough 2026-07-17, TechReq#6): PCB accepts an ID expired within ~6 months
 * and flags it for update rather than turning the customer away. Admin-flippable.
 */
export const ACCEPT_EXPIRED_GRACE_MONTHS_KEY = 'cip.existingCustomer.acceptExpiredGraceMonths';
export const DEFAULT_ACCEPT_EXPIRED_GRACE_MONTHS = 6;

function isoToday(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

/**
 * ISO YYYY-MM-DD for the date `months` calendar-months before today — the floor
 * of the "recently expired, still acceptable" window (WT-BACKLOG C2). An ID
 * expiry >= this floor is within grace; older is beyond grace. `months <= 0`
 * collapses the window to today, i.e. strict (any expired ID is beyond grace).
 */
function graceFloorIso(months: number): string {
  const n = new Date();
  if (months > 0) n.setMonth(n.getMonth() - months);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

/**
 * Derive the Primary Photo ID gating for a person. Existing customers get the
 * reduced path (see block comment above); a fresh add always returns
 * 'required'. `strategy` selects the expiring-soon behavior (admin-config).
 */
export function derivePrimaryIdGating(
  person: RaoPerson,
  warningDays: number,
  strategy: ExistingIdExpiringSoonGate,
  graceMonths: number = DEFAULT_ACCEPT_EXPIRED_GRACE_MONTHS,
): PrimaryIdGating {
  if (!isExistingCustomer(person)) return 'required';

  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const idExpiryIso = typeof extra.idExpiryDate === 'string' ? extra.idExpiryDate.trim() : '';
  // No ID expiry on file → cannot confirm a current ID is on record → required
  // (covers the legacy-CIF-with-no-CIP case; Bill: "required when expired OR
  // missing"). The existing-CIF load populates F258 when an ID IS on file.
  if (!idExpiryIso) return 'required';

  const todayIso = isoToday();
  if (idExpiryIso < todayIso) {
    // Expired. Within the admin grace window (C2, default 6mo per Collette) it's
    // 'recommended' (yellow "Update recommended" + follow-up, not a Continue-
    // block); beyond it — or grace 0 (strict) — it's a 'required' hard-block.
    return idExpiryIso >= graceFloorIso(graceMonths) ? 'recommended' : 'required';
  }

  const warnFloor = new Date(Date.now() + warningDays * 24 * 60 * 60 * 1000);
  const warnIso = `${warnFloor.getFullYear()}-${String(warnFloor.getMonth() + 1).padStart(2, '0')}-${String(warnFloor.getDate()).padStart(2, '0')}`;
  if (idExpiryIso <= warnIso) return strategy === 'hard-stop' ? 'required' : 'recommended';

  return 'satisfied';
}

/**
 * Read the admin-configurable expiring-soon gate from the config bundle's
 * systemSettings (flat record OR array-of-{key,value}, mirroring
 * readIdExpirationWarningDays). Falls back to the recommend default.
 */
export function readExistingIdExpiringSoonGate(systemSettings: unknown): ExistingIdExpiringSoonGate {
  const probe = (v: unknown): ExistingIdExpiringSoonGate | null => {
    if (typeof v !== 'string') return null;
    const s = v.trim().toLowerCase();
    return s === 'hard-stop' || s === 'recommend' ? (s as ExistingIdExpiringSoonGate) : null;
  };
  // config.systemSettings is the /v3/config/system-settings payload, which is
  // an ARRAY of { settingKey, settingValue } rows at runtime (despite its
  // Record<> type). Tolerate that AND a flat record AND the legacy {key,value}.
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = typeof row.settingKey === 'string' ? row.settingKey
        : typeof row.key === 'string' ? row.key : undefined;
      if (k === EXISTING_ID_EXPIRING_SOON_GATE_KEY) {
        const g = probe(row.settingValue ?? row.value);
        if (g !== null) return g;
      }
    }
  } else if (systemSettings && typeof systemSettings === 'object') {
    const g = probe((systemSettings as Record<string, unknown>)[EXISTING_ID_EXPIRING_SOON_GATE_KEY]);
    if (g !== null) return g;
  }
  return DEFAULT_EXISTING_ID_EXPIRING_SOON_GATE;
}

/**
 * Read the admin-configurable accept-expired grace window (MONTHS) from the
 * config bundle's systemSettings (array-of-{key,value} OR flat record, mirroring
 * {@link readExistingIdExpiringSoonGate}). Non-negative integer; falls back to
 * {@link DEFAULT_ACCEPT_EXPIRED_GRACE_MONTHS}. WT-BACKLOG C2.
 */
export function readAcceptExpiredGraceMonths(systemSettings: unknown): number {
  const probe = (v: unknown): number | null => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.trim()) : NaN;
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
  };
  if (Array.isArray(systemSettings)) {
    for (const row of systemSettings as ReadonlyArray<Record<string, unknown>>) {
      if (!row || typeof row !== 'object') continue;
      const k = typeof row.settingKey === 'string' ? row.settingKey
        : typeof row.key === 'string' ? row.key : undefined;
      if (k === ACCEPT_EXPIRED_GRACE_MONTHS_KEY) {
        const g = probe(row.settingValue ?? row.value);
        if (g !== null) return g;
      }
    }
  } else if (systemSettings && typeof systemSettings === 'object') {
    const g = probe((systemSettings as Record<string, unknown>)[ACCEPT_EXPIRED_GRACE_MONTHS_KEY]);
    if (g !== null) return g;
  }
  return DEFAULT_ACCEPT_EXPIRED_GRACE_MONTHS;
}

/**
 * Verify-tab rollup status for the existing-customer reduced PDW path.
 * 'red' = hard-block, 'yellow' = warn-but-allow, 'green' = clean.
 */
export type VerifyTabStatus = 'green' | 'yellow' | 'red';

export interface VerifyTabDerivation {
  readonly status: VerifyTabStatus;
  /** Short one-line reason, suitable for a tooltip / hint surface. */
  readonly reason: string;
}

interface VerifyTabInputs {
  readonly idExpiryIso: string | undefined;
  readonly warningDays: number;
  /** Accept-expired grace window in months (C2). 0 = strict. */
  readonly graceMonths: number;
  readonly amlScore: number | undefined;
  readonly ofacStatus: OfacStatus;
}

/**
 * Pure derivation — separated from `deriveVerifyTabStatus` so tests
 * can drive the rules without spinning up a full RaoPerson.
 */
function deriveVerifyTabStatusFromInputs(input: VerifyTabInputs): VerifyTabDerivation {
  const { idExpiryIso, warningDays, graceMonths, amlScore, ofacStatus } = input;

  // Today as ISO YYYY-MM-DD; lex-compare against idExpiryIso.
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const todayIso = `${yyyy}-${mm}-${dd}`;

  // 1. CIP expired. Within the admin grace window (C2, default 6mo per Collette)
  //    → yellow "Update recommended" (allow + follow-up); beyond it, or grace 0
  //    (strict), → red hard-block "Required".
  if (idExpiryIso && idExpiryIso < todayIso) {
    if (graceMonths > 0 && idExpiryIso >= graceFloorIso(graceMonths)) {
      return {
        status: 'yellow',
        reason: `Primary ID expired ${idExpiryIso} — update recommended (within ${graceMonths}-month grace); capture a follow-up.`,
      };
    }
    return { status: 'red', reason: `Primary ID expired ${idExpiryIso} — required: re-capture before commit.` };
  }

  // 2. CIP within warning window → yellow.
  if (idExpiryIso) {
    const warnFloor = new Date(now.getTime() + warningDays * 24 * 60 * 60 * 1000);
    const wYyyy = warnFloor.getFullYear();
    const wMm = String(warnFloor.getMonth() + 1).padStart(2, '0');
    const wDd = String(warnFloor.getDate()).padStart(2, '0');
    const warnIso = `${wYyyy}-${wMm}-${wDd}`;
    if (idExpiryIso <= warnIso) {
      return {
        status: 'yellow',
        reason: `Primary ID expires ${idExpiryIso} (within ${warningDays}-day window) — consider re-capture.`,
      };
    }
  }

  // 3. OFAC hard-block.
  if (ofacStatus === 'hit-current') {
    return { status: 'red', reason: 'Prior OFAC scan flagged a current SDN sanctions match.' };
  }

  // 4. Existing-customer baseline (F1, Bill 2026-06-03; per
  //    feedback_existing_customer_skip_ln_and_ofac_20260521). LN + OFAC are
  //    NOT re-required for an on-file CIF — we trust the prior-pass baseline
  //    and gate ONLY on CIP-expiration (handled in rules 1-3) + a current
  //    OFAC sanctions hit (rule 3). The ABSENCE of a prior LN/OFAC run must
  //    NOT downgrade — that was the procedure breach (existing CIF re-required
  //    CIP capture + LN). Only an ACTUAL adverse prior result on file (LN
  //    score below the safe band, or an OFAC scan still pending) surfaces a
  //    soft yellow; everything else is green.
  const priorLnAdverse = typeof amlScore === 'number' && amlScore < 750;
  const priorOfacPending = ofacStatus === 'pending';
  if (!priorLnAdverse && !priorOfacPending) {
    return {
      status: 'green',
      reason: idExpiryIso
        ? 'Existing customer — CIP current; LN/OFAC not re-required (trusted baseline).'
        : 'Existing customer — LN/OFAC not re-required (trusted baseline).',
    };
  }

  // 5. An actual adverse prior result is on file → soft yellow (surface, allow).
  const flags: string[] = [];
  if (priorLnAdverse) flags.push(`prior LN score ${amlScore} below the safe band (750)`);
  if (priorOfacPending) flags.push('prior OFAC scan still pending');
  return {
    status: 'yellow',
    reason: `Verify — ${flags.join('; ')}.`,
  };
}

/**
 * Top-level helper. Pulls the four signals off the RaoPerson + applies
 * `deriveVerifyTabStatusFromInputs`. Caller passes the resolved warning
 * window so the SystemSettings probe happens once at the dialog level
 * instead of per-render in this helper.
 */
export function deriveVerifyTabStatus(
  person: RaoPerson,
  warningDays: number,
  graceMonths: number = DEFAULT_ACCEPT_EXPIRED_GRACE_MONTHS,
): VerifyTabDerivation {
  const extra = person.extra ?? {};
  const idExpiryRaw = typeof extra.idExpiryDate === 'string' ? (extra.idExpiryDate as string) : '';
  const ofacStatusRaw = typeof extra.ofacStatus === 'string' ? (extra.ofacStatus as string) : undefined;

  return deriveVerifyTabStatusFromInputs({
    idExpiryIso: idExpiryRaw || undefined,
    warningDays,
    graceMonths,
    amlScore: person.amlInsightScore,
    ofacStatus: normalizeOfacStatus(ofacStatusRaw),
  });
}

/**
 * Bridge BACKLOG-0513 signals (CIP expiration + amlScore + ofacStatus) into the
 * BACKLOG-0017 VerifyClearanceMap shape so VerifyRollupBanner renders real
 * per-section state instead of "unrun" pills on first paint. Explicit
 * `person.extra.verifyClearance[section]` entries (set by FSR override /
 * comp-doc clearance / LN import) take precedence over these derived values.
 *
 * `channel` (Bill 2026-06-03) — drives the email-optional rule. In-branch
 * (the ROP-teams FSR default) allows an EMPTY email to clear as 'pass'
 * (allowed-empty): a customer opening a Free Checking account in person with no
 * online banking / e-statements is not required to have an email, and the
 * Verify tab must not block on it. Online channel always requires email.
 * NOTE: product-level "requires email" (e.g. Ultimate Checking / e-statements)
 * is enforced separately at product/commit validation — this derivation only
 * stops an empty email from leaving the Verify tab stuck "unrun".
 */
export function deriveVerifyClearanceMap(
  person: RaoPerson,
  warningDays: number,
  channel: 'In-Branch' | 'Online' = 'In-Branch',
): VerifyClearanceMap {
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const idExpiryIso = typeof extra.idExpiryDate === 'string' ? (extra.idExpiryDate as string) : undefined;
  const ofacStatus = normalizeOfacStatus(typeof extra.ofacStatus === 'string' ? (extra.ofacStatus as string) : undefined);
  const amlScore = person.amlInsightScore;

  const now = new Date();
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const warnFloor = new Date(now.getTime() + warningDays * 24 * 60 * 60 * 1000);
  const warnIso = `${warnFloor.getFullYear()}-${String(warnFloor.getMonth() + 1).padStart(2, '0')}-${String(warnFloor.getDate()).padStart(2, '0')}`;

  const derived: VerifyClearanceMap = {};

  // idScan — driven by primary-ID expiration only (per spec, idScan cannot
  // be cleared via comp-doc; only by primary-ID re-capture).
  if (idExpiryIso) {
    let state: SectionClearance['state'] = 'pass';
    if (idExpiryIso < todayIso) state = 'fail';
    else if (idExpiryIso <= warnIso) state = 'pending'; // within warn window — flag but allow
    // reason marks this as the on-file ID's expiry date, not a scan (Bill 2026-10-06).
    derived.idScan = { section: 'idScan', state, clearedAt: undefined, reason: 'on-file-expiry' };
  }

  // ofac — derived from prior scan result.
  if (ofacStatus !== 'unrun') {
    const state: SectionClearance['state'] =
      ofacStatus === 'hit-current' ? 'fail'
      : (ofacStatus === 'clear' || ofacStatus === 'archived-only') ? 'pass'
      : 'pending';
    derived.ofac = { section: 'ofac', state };
  }

  // ln — derived from the FraudPoint score band (per alpha.97 thresholds).
  // Bill 2026-06-12: the Moderate band must REFLECT follow-up satisfaction
  // rather than parking the section on `pending` forever. Low (>=750) = pass;
  // High (<500) = fail. Moderate (500-749) = pass once the market-correct
  // required follow-up items are captured (deriveFraudPointFollowUp mirrors the
  // BE FraudPointRiskGateService); pending while any required item is unmet —
  // NOT a hard fail, so the supervisor-override path stays (the FraudPoint score
  // itself remains the canonical gate; <500 still fails).
  if (typeof amlScore === 'number') {
    let state: SectionClearance['state'] = 'pending';
    if (amlScore >= 750) {
      state = 'pass';                              // Safe / low-risk band
    } else if (amlScore >= 500) {
      const followUp = deriveFraudPointFollowUp(person);
      state = followUp?.allRequiredSatisfied ? 'pass' : 'pending';
    } else {
      state = 'fail';                              // High-risk / Caution band
    }
    derived.ln = { section: 'ln', state };
  }

  // email — allowed-empty for in-branch (Bill 2026-06-03). An empty email on the
  // FSR in-branch tool clears as 'pass' so the Verify tab doesn't block a Free
  // Checking opening with no email. A populated email is left undefined here
  // (no auto-gate) so LN-import / FSR review can set the real state. Online
  // channel never auto-passes — email is required there.
  if (channel === 'In-Branch') {
    const email = (person.email ?? '').trim();
    if (email.length === 0) {
      // reason tells the chip to say "not required" - nothing was checked (CHG-0051).
      derived.email = { section: 'email', state: 'pass', reason: 'not-required' };
    }
  }

  return derived;
}

